import * as React from 'react';
import Step, { PrivateStepState, SharedStepState, StepProperties } from '../Step';
import { RightArrowIcon } from '../../common/Icons';
import { NameRequirementManager }  from '../../common/requirementManagers/NameRequirementManager';
import Slider from 'rc-slider';
import {
    LowercaseLettersNumbersHyphensValidator,
    NoDashSequenceValidator,
    StartEndValidator,
    StringLengthValidator
} from '../../../services/nameValidation';
import FlowManager from '../../common/FlowManager';
import { BUCKET_MIN_ALLOWED_SYMBOLS } from '../../../utils/constants';
import { capitalizeFirstLetter } from '../../../utils/utilFunctions';
import 'rc-slider/assets/index.css';
import { TransitionType } from '../../../utils/types';

const BLOCK_SIZE_VALUES = [64, 128, 256, 512, 1024, 2048, 4096, 8192].map(sizeInKib => sizeInKib * 1024);

const KIB_256 = 256 * 1024;
const KIB_512 = 512 * 1024;
const SLIDER_MARKS = {
    0: '64',
    1: '128',
    2: '256',
    3: '512',
    4: '1',
    5: '2',
    6: '4',
    7: '8'
};

interface AdvancedStepProperties extends StepProperties<AdvancedSettingsSharedStepState> {
    storageContainerName: string;
    isLucidLink: boolean;
    transitionType: TransitionType;
    defaultBlockSize: number;
    maxBucketNameLength: number;
}

export interface AdvancedSettingsSharedStepState extends SharedStepState {
    bucketName: string;
    ownBucketNameProvided: boolean;
    blockSize: number | null;
    sliderIdx: number | null;
    blockSizeText: string | null;
    shouldIncludeBlockSize: boolean;
    // isV4Selected: boolean;
}

interface AdvancedSettingsPrivateStepState extends PrivateStepState { }

interface AdvancedSettingsStepState extends AdvancedSettingsPrivateStepState, AdvancedSettingsSharedStepState { }

class AdvancedSettingsStepComponent extends Step<
    AdvancedStepProperties,
    AdvancedSettingsStepState,
    AdvancedSettingsSharedStepState
> {
    private requirementManager: NameRequirementManager = new NameRequirementManager();
    private flowManager: FlowManager;
    private currentSliderIdx: number;

    constructor(props: any) {
        super(props);

        this.onSliderChange = this.onSliderChange.bind(this);
        this.handleUserInputChanged = this.handleUserInputChanged.bind(this);
        this.handleUserInputChangedSkip = this.handleUserInputChangedSkip.bind(this);
        this.handleUserInputChangedNext = this.handleUserInputChangedNext.bind(this);

        this.getDefaultSliderIndex = this.getDefaultSliderIndex.bind(this);

        this.flowManager = new FlowManager(this.requirementManager.requirementsAreMet, this.completeStep);
        this.flowManager.registerListeners();

        this.setValidators();
        const savedBlockSize = this.props.savedState.blockSize;
        const currentSliderIdx = this.props.savedState.sliderIdx;
        this.currentSliderIdx = currentSliderIdx === null ? this.getDefaultSliderIndex() : currentSliderIdx;

        this.state = {
            ...this.props.savedState,
            blockSize: savedBlockSize === null ? BLOCK_SIZE_VALUES[this.getDefaultSliderIndex()] : savedBlockSize,
            blockSizeText: this.getCurrentBlockSizeText(),
            sliderIdx: this.currentSliderIdx
        };
    }

    render() {
        return (
            <div className="col-12 col-lg-6 col-xl-6 offset-lg-1 d-flex align-items-center input-step">
                <form>
                    {this.renderBucketName()}
                    {this.renderSlider()}

                    <button
                        type="button"
                        disabled={!this.isNextStepAllowed()}
                        className="btn step-btn float-left mobile__float"
                        onClick={this.handleUserInputChangedNext}
                    >
                        <span>Next</span>
                        <RightArrowIcon width="13px" height="13px" />
                    </button>
                    <button
                        type="button"
                        name="skipbutton"
                        className="btn step-btn btn-outline-primary float-left mobile__float"
                        onClick={this.handleUserInputChangedSkip}
                    >
                        <span>Skip</span>
                        <RightArrowIcon width="13px" height="13px" />
                    </button>
                </form>
            </div>
        );
    }

    componentWillUnmount() {
        this.flowManager.unregisterListeners();
    }

    protected isNextStepAllowed() {
        return this.state.bucketName === '' ||
         (!this.props.isLucidLink && this.requirementManager.requirementsAreMet());
    }

    protected shouldShowWarning() {
        return this.getCurrentSliderValue() !== KIB_256;
    }

    protected getCurrentSliderValue() {
        return BLOCK_SIZE_VALUES[this.currentSliderIdx];
    }

    protected getCurrentSliderMark() {
        return SLIDER_MARKS[this.currentSliderIdx];
    }

    protected getCurrentBlockSizeText() {
        return this.getCurrentSliderMark() + (this.getCurrentSliderValue() > KIB_512 ? 'MB' : 'KB');
    }

    protected onSliderChange(idx: number) {
        this.currentSliderIdx = idx;
        this.setState({
            blockSize: this.getCurrentSliderValue(),
            sliderIdx: idx,
            blockSizeText: this.getCurrentBlockSizeText(),
        });
    }

    protected renderSlider() {
        const blockSizeLink = 'https://support.lucidlink.com/hc/en-us/articles' +
            '/5778284033293-Choosing-a-LucidLink-Block-Size';
        return (
            <div className="slider-container">
                <div className="range">
                    <div>
                        <label htmlFor="block_size">
                            Block size<em>|</em>
                            <a href={blockSizeLink} target="_blank">
                                How to choose Filespace block size?
                            </a>
                        </label>
                        <h3>{this.state.blockSizeText}</h3>
                    </div>
                    <Slider
                        min={0}
                        max={BLOCK_SIZE_VALUES.length - 1}
                        marks={SLIDER_MARKS}
                        onChange={this.onSliderChange}
                        included={false}
                        defaultValue={this.currentSliderIdx}
                    />
                    <div className="values">
                        <span>KB</span>
                        <span className="green">MB</span>
                    </div>
                </div>
            </div>
        );
    }

    protected renderBucketName() {
        if (this.props.isLucidLink) {
            return null;
        }
        return (
            <div>
                <div className="float-label-control large row">
                    <input
                        autoFocus={true}
                        type="text"
                        className="form-control col-12"
                        onChange={this.handleUserInputChanged}
                        placeholder={this.props.storageContainerName + ' name'}
                        name="bucketname"
                        id="bucketname"
                        value={this.state.bucketName}
                    />
                    <label htmlFor="ep-url" className="ttip top-left">
                        <em>{capitalizeFirstLetter(this.props.storageContainerName)} name</em>
                        {this.getBucketOrContainerNameTooltip()}
                    </label>
                </div>
                {this.requirementManager.getRequirements(this.state.bucketName)}
            </div>
        );
    }

    protected getBucketOrContainerNameTooltip() {
        return this.props.storageContainerName === 'container' ?
        (
            <div>Type the container name that you want to use for this filespace.
            It’s <b>highly recommended that this container is empty, soft delete is disabled</b>
            and container is not used for any other purposes.
            </div>
        ) :
        (
            <div>Type the bucket name that you want to use for this filespace.
                It’s <b>highly recommended that this bucket is empty, versioning is disabled</b>
                and bucket is not used for any other purposes.
            </div>
        );
    }

    protected getSharedStepState(): AdvancedSettingsSharedStepState {
        return {
            bucketName: this.state.bucketName,
            ownBucketNameProvided: this.state.ownBucketNameProvided,
            blockSize: this.state.blockSize,
            sliderIdx: this.state.sliderIdx,
            shouldIncludeBlockSize: this.getCurrentSliderValue() !== KIB_256,
            blockSizeText: this.state.blockSizeText,
            // isV4Selected: this.state.isV4Selected
        };
    }

    private handleUserInputChanged(e: any): void {
        if (e.target.type === 'text') {
            this.setState({ bucketName: e.target.value.trim() });
        } else {
            this.setState({ ownBucketNameProvided: true });
        }
    }

    private handleUserInputChangedNext(): void {
        this.setState(
            { ownBucketNameProvided: (this.state.bucketName === '' || this.props.isLucidLink) ? false : true },
            () => {
                this.completeStep();
            });
    }

    private handleUserInputChangedSkip(): void {
        this.setState(
            {
                ownBucketNameProvided: false,
                blockSizeText: null,
                blockSize: null,
                sliderIdx: null
            },
            () => {
                this.completeStep();
            });
    }

    private setValidators(): void {
        if (this.props.isLucidLink) {
            this.requirementManager.setValidators([]);
            return;
        }
        this.requirementManager.setValidators([
            new StartEndValidator(),
            new LowercaseLettersNumbersHyphensValidator(),
            new NoDashSequenceValidator(),
            new StringLengthValidator(BUCKET_MIN_ALLOWED_SYMBOLS, this.props.maxBucketNameLength)
        ]);
    }

    private getDefaultSliderIndex(): number {
        return BLOCK_SIZE_VALUES.indexOf(this.props.defaultBlockSize);
    }
}

export default AdvancedSettingsStepComponent;



// WEBPACK FOOTER //
// ./src/webportal/components/wizard/components/AdvancedSettingsStep.tsx